import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Building2, LogIn, Settings2, Users, ClipboardList, ListChecks } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/AppLayout";
import { MODULE_LABELS } from "../components/shared";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const PLANS = ["trial", "standard", "enterprise"];

export default function Clients() {
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState(null);
  const [meta, setMeta] = useState({ toggleable: [], providers: [], plans: {} });
  const [onboard, setOnboard] = useState(false);
  const [manage, setManage] = useState(null);
  const [manageUsers, setManageUsers] = useState([]);
  const [form, setForm] = useState(null);

  const load = () => api.get("/admin/clients").then((r) => setClients(r.data));
  useEffect(() => { load(); api.get("/meta/modules").then((r) => setMeta(r.data)); }, []);

  const modulesForPlan = (plan) => {
    const enabled = meta.plans?.[plan] || [];
    return Object.fromEntries(meta.toggleable.map((m) => [m, enabled.includes(m)]));
  };

  const newClientForm = () => ({
    org_name: "", plan: "standard", admin_name: "", admin_email: "", admin_password: "Welcome!2026",
    modules: modulesForPlan("standard"),
    marketplace_providers: [...meta.providers],
  });

  const createClient = async () => {
    if (!form.org_name || !form.admin_email) return toast.error("Org name and admin email required");
    try { await api.post("/admin/clients", form); toast.success("Client onboarded"); setOnboard(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openManage = async (c) => {
    setManage({ ...c });
    const { data } = await api.get(`/admin/clients/${c.id}/users`);
    setManageUsers(data);
  };

  const saveManage = async () => {
    await api.patch(`/admin/clients/${manage.id}`, { modules: manage.modules, marketplace_providers: manage.marketplace_providers, plan: manage.plan });
    toast.success("Client updated"); setManage(null); load();
  };

  const toggleActive = async (c) => { await api.patch(`/admin/clients/${c.id}`, { active: !c.active }); toast.success(c.active ? "Client deactivated" : "Client activated"); load(); };
  const toggleUser = async (u) => { await api.patch(`/admin/users/${u.id}`, { active: !u.active }); setManageUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, active: !x.active } : x)); };

  const enter = async (c) => { await impersonate(c.id); toast.success(`Entered ${c.name}`); navigate("/app"); };

  if (!clients) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" /></div>;

  return (
    <div>
      <PageHeader title="Clients" subtitle="Onboard and manage every client organisation on the platform.">
        <Button className="rounded-full" onClick={() => { setForm(newClientForm()); setOnboard(true); }} data-testid="onboard-client-btn"><Plus className="mr-2 h-4 w-4" /> Onboard Client</Button>
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {clients.map((c) => (
          <Card key={c.id} className={`rounded-2xl border-border p-5 ${!c.active && "opacity-60"}`} data-testid={`client-card-${c.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]"><Building2 className="h-5 w-5" /></div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="rounded-full capitalize">{c.plan}</Badge>
                <Badge className={`rounded-full ${c.active ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-red-500/10 text-red-600 dark:text-red-400"}`}>{c.active ? "Active" : "Inactive"}</Badge>
              </div>
            </div>
            <div className="mt-4 font-heading text-base font-semibold">{c.name}</div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <Mini icon={Users} label="Users" value={c.user_count} />
              <Mini icon={ClipboardList} label="Reports" value={c.report_count} />
              <Mini icon={ListChecks} label="Actions" value={c.action_count} />
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" className="flex-1 rounded-full text-xs" onClick={() => enter(c)} data-testid={`enter-client-${c.id}`}><LogIn className="mr-1 h-3 w-3" /> Enter</Button>
              <Button size="sm" variant="outline" className="flex-1 rounded-full text-xs" onClick={() => openManage(c)} data-testid={`manage-client-${c.id}`}><Settings2 className="mr-1 h-3 w-3" /> Manage</Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Onboard dialog */}
      <Dialog open={onboard} onOpenChange={setOnboard}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Onboard New Client</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Organisation name</Label><Input value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} data-testid="onboard-org" /></div>
                <div className="space-y-1.5"><Label>Plan</Label>
                  <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v, modules: modulesForPlan(v) })}><SelectTrigger data-testid="onboard-plan"><SelectValue /></SelectTrigger>
                    <SelectContent>{PLANS.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
              <div className="rounded-xl border border-border p-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">First Client Admin</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Name</Label><Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} data-testid="onboard-admin-name" /></div>
                  <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} data-testid="onboard-admin-email" /></div>
                </div>
                <div className="mt-3 space-y-1.5"><Label>Temporary password</Label><Input value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} data-testid="onboard-admin-pw" /></div>
              </div>
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Enabled modules</div>
                <div className="grid grid-cols-2 gap-2">
                  {meta.toggleable.map((m) => (
                    <label key={m} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm">
                      <Checkbox checked={form.modules[m]} onCheckedChange={(v) => setForm({ ...form, modules: { ...form.modules, [m]: !!v } })} data-testid={`onboard-mod-${m}`} />
                      {MODULE_LABELS[m]}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Marketplace providers</div>
                <div className="flex gap-2">
                  {meta.providers.map((p) => {
                    const on = form.marketplace_providers.includes(p);
                    return (
                      <button key={p} type="button" onClick={() => setForm({ ...form, marketplace_providers: on ? form.marketplace_providers.filter((x) => x !== p) : [...form.marketplace_providers, p] })}
                        className={`rounded-full border px-4 py-1.5 text-sm ${on ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]" : "border-border text-muted-foreground"}`} data-testid={`onboard-prov-${p}`}>{p}</button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={createClient} className="rounded-full" data-testid="onboard-save-btn">Create Client</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage dialog */}
      <Dialog open={!!manage} onOpenChange={(o) => !o && setManage(null)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          {manage && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{manage.name}</DialogTitle></DialogHeader>
              <Tabs defaultValue="modules">
                <TabsList className="rounded-full">
                  <TabsTrigger value="modules" className="rounded-full" data-testid="manage-tab-modules">Modules</TabsTrigger>
                  <TabsTrigger value="market" className="rounded-full" data-testid="manage-tab-market">Marketplace</TabsTrigger>
                  <TabsTrigger value="users" className="rounded-full" data-testid="manage-tab-users">Users</TabsTrigger>
                </TabsList>
                <TabsContent value="modules" className="mt-4 space-y-2">
                  <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/40 px-3 py-2.5">
                    <div>
                      <div className="text-sm font-medium">Subscription plan</div>
                      <div className="text-xs text-muted-foreground">Changing the plan resets modules to its defaults.</div>
                    </div>
                    <Select value={manage.plan} onValueChange={(v) => setManage({ ...manage, plan: v, modules: modulesForPlan(v) })}>
                      <SelectTrigger className="w-36 capitalize" data-testid="manage-plan"><SelectValue /></SelectTrigger>
                      <SelectContent>{PLANS.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {meta.toggleable.map((m) => (
                    <div key={m} className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5 text-sm">
                      {MODULE_LABELS[m]}
                      <Switch checked={manage.modules?.[m] !== false} onCheckedChange={(v) => setManage({ ...manage, modules: { ...manage.modules, [m]: v } })} data-testid={`manage-mod-${m}`} />
                    </div>
                  ))}
                </TabsContent>
                <TabsContent value="market" className="mt-4">
                  <p className="mb-3 text-xs text-muted-foreground">Providers available to this client in the Marketplace.</p>
                  <div className="flex gap-2">
                    {meta.providers.map((p) => {
                      const on = (manage.marketplace_providers || []).includes(p);
                      return (
                        <button key={p} type="button" onClick={() => setManage({ ...manage, marketplace_providers: on ? manage.marketplace_providers.filter((x) => x !== p) : [...(manage.marketplace_providers || []), p] })}
                          className={`rounded-full border px-4 py-1.5 text-sm ${on ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]" : "border-border text-muted-foreground"}`} data-testid={`manage-prov-${p}`}>{p}</button>
                      );
                    })}
                  </div>
                </TabsContent>
                <TabsContent value="users" className="mt-4 space-y-2">
                  {manageUsers.map((u) => (
                    <div key={u.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5 text-sm" data-testid={`manage-user-${u.email}`}>
                      <div><div className="font-medium">{u.name}</div><div className="text-xs text-muted-foreground">{u.email} · {u.role}</div></div>
                      <Switch checked={u.active !== false} onCheckedChange={() => toggleUser(u)} />
                    </div>
                  ))}
                </TabsContent>
              </Tabs>
              <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
                <Button variant="outline" className="rounded-full text-destructive" onClick={() => toggleActive(manage)} data-testid="toggle-client-active">
                  {manage.active ? "Deactivate client" : "Activate client"}
                </Button>
                <Button onClick={saveManage} className="rounded-full" data-testid="manage-save-btn">Save changes</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Mini = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl bg-secondary/60 py-2">
    <div className="font-heading text-lg font-bold">{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
  </div>
);
